import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useOrganization } from "../context/OrganizationContext";
import apiService from "../services/apiService";
import { extractSubscriptionList, isFreeTierOrNoSubscription } from "../utils/subscription";
import { mergeAnalyticsPayloads } from "../utils/mergeAnalytics";

/**
 * Shown on dashboard when the org is on free / no paid plan and usage has exceeded API limits.
 */
export default function FreeTierUpgradeBanner() {
  const { subscription, currentOrganization } = useOrganization();
  const [overLimit, setOverLimit] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!currentOrganization?.id || !isFreeTierOrNoSubscription(subscription)) {
      setOverLimit(false);
      setChecked(true);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const rawProjects = await apiService.getProjects(currentOrganization.id);
        const projects = extractSubscriptionList(rawProjects);
        const keyLists = await Promise.all(
          (projects || []).map((p) =>
            apiService.getApiKeys(currentOrganization.id, p.uuid),
          ),
        );
        const keys = keyLists.flatMap((k) => extractSubscriptionList(k));
        if (!keys.length) {
          if (!cancelled) {
            setOverLimit(false);
            setChecked(true);
          }
          return;
        }
        const results = await Promise.all(
          keys.map((k) => apiService.getApiKeyAnalytics(k.uuid, "30d")),
        );
        const analytics = mergeAnalyticsPayloads(results);
        const L = analytics?.limits;
        const monthlyLimit = L?.monthly_limit;
        const dailyLimit = L?.daily_limit;
        const monthlyUsed = Number(L?.monthly_used) || 0;
        const dailyUsed = Number(L?.daily_used) || 0;

        let exceeded = false;
        if (monthlyLimit != null && Number(monthlyLimit) > 0) {
          if (monthlyUsed >= Number(monthlyLimit)) exceeded = true;
        }
        if (dailyLimit != null && Number(dailyLimit) > 0) {
          if (dailyUsed >= Number(dailyLimit)) exceeded = true;
        }

        if (!cancelled) {
          setOverLimit(exceeded);
          setChecked(true);
        }
      } catch {
        if (!cancelled) {
          setOverLimit(false);
          setChecked(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [subscription?.id, currentOrganization?.id]);

  if (!checked || !overLimit) return null;

  return (
    <div className="sticky top-0 z-30 border-b border-red-500/30 bg-red-950/40 px-4 py-3 text-center sm:px-6">
      <p className="font-secondary text-sm text-red-100/95">
        <span className="font-semibold text-white">Usage limit reached</span>
        {" — "}
        Your organization is on the free tier and has hit the included quota.{" "}
        <Link
          to="/settings"
          className="font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:text-primary-hover"
        >
          Choose a plan
        </Link>{" "}
        to continue without interruption.
      </p>
    </div>
  );
}
